import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar, Box, Button, Chip, CircularProgress, Drawer, IconButton, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import { Close, CloudOff, EmojiEvents, Refresh, Storefront } from '@mui/icons-material';
import { useGetIncentiveLeaderboardQuery } from '../../../services/salesIncentivesApi';
import { incentivesHidden } from '../../../store/salesIncentivesSlice';
import { isIncentiveModuleError } from '../../../utils/incentives';

const PLAN_TYPE = { salesperson: 'Salesperson plan', store: 'Store plan', region: 'Region plan' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const todayIst = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

function endsLabel(periodEnd) {
  if (!periodEnd) return '';
  const [y, m, d] = periodEnd.split('-').map(Number);
  const [ty, tm, td] = todayIst().split('-').map(Number);
  const days = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
  if (days === 0) return 'ends today';
  if (days < 0) return `ended ${d} ${MONTHS[m - 1]}`;
  return `ends ${d} ${MONTHS[m - 1]} (${days} day${days === 1 ? '' : 's'})`;
}
const pctOf = (v) => (v === null || v === undefined ? null : parseFloat(v));
const inr = (v) => `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const timeLabel = (d) => d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });

function Bar({ pct }) {
  const v = pct ?? 0;
  return (
    <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, v))} aria-label={pct === null ? 'No target' : `${Math.round(v)}%`}
      sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: v >= 100 ? '#16A34A' : '#4F46E5' } }} />
  );
}

// Store leaderboard: plans live at this store, people ranked by payout then % achieved. Fetched fresh on open.
export default function IncentivesDrawer({ open, onClose }) {
  const dispatch = useDispatch();
  const me = useSelector((s) => s.auth.user?.id);
  const storeName = useSelector((s) => s.auth.store?.name);
  const { data: plans = [], isLoading, isFetching, error, refetch, fulfilledTimeStamp } =
    useGetIncentiveLeaderboardQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true });
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    if (isIncentiveModuleError(error)) { dispatch(incentivesHidden()); onClose(); }
  }, [error, dispatch, onClose]);

  const plan = useMemo(() => plans.find((p) => p.plan_id === planId) || plans[0], [plans, planId]);
  const pooled = plan && plan.plan_type !== 'salesperson';

  const body = () => {
    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
          <CloudOff sx={{ fontSize: 44, color: '#CBD5E1', mb: 1 }} />
          <Typography fontWeight={700} fontSize="0.9rem" color="#64748B">Couldn't load incentives.</Typography>
          <Typography fontSize="0.78rem" color="#94A3B8" sx={{ mb: 2 }}>Check the connection and try again.</Typography>
          <Button variant="outlined" onClick={refetch} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>Retry</Button>
        </Box>
      );
    }
    if (!plans.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 8, px: 2, color: '#CBD5E1' }}>
          <EmojiEvents sx={{ fontSize: 52, mb: 1.5, opacity: 0.5 }} />
          <Typography fontWeight={700} fontSize="0.95rem" color="#94A3B8">No incentive plans running at this store right now.</Typography>
        </Box>
      );
    }
    return (
      <Box>
        {plans.length > 1 && (
          <Stack direction="row" spacing={0.75} useFlexGap
            sx={{ flexWrap: { xs: 'nowrap', sm: 'wrap' }, overflowX: { xs: 'auto', sm: 'visible' }, pb: 1, mb: 1.5,
              '&::-webkit-scrollbar': { display: 'none' }, rowGap: 0.75 }}>
            {plans.map((p) => {
              const on = p.plan_id === plan.plan_id;
              return (
                <Chip key={p.plan_id} clickable label={p.name} onClick={() => setPlanId(p.plan_id)} aria-pressed={on}
                  sx={{ flexShrink: 0, maxWidth: 220, height: { xs: 36, sm: 30 }, fontWeight: 700, fontSize: '0.72rem', borderRadius: '8px',
                    bgcolor: on ? '#EEF2FF' : '#FFF', color: on ? '#4F46E5' : '#64748B', border: `1px solid ${on ? '#4F46E5' : '#E2E8F0'}` }} />
              );
            })}
          </Stack>
        )}
        <Typography fontWeight={700} fontSize="0.875rem" color="#1E293B" noWrap title={plan.name}>{plan.name}</Typography>
        <Typography fontSize="0.72rem" fontWeight={600} color="#64748B" sx={{ mb: 1.5 }}>
          {PLAN_TYPE[plan.plan_type] || 'Plan'} · {endsLabel(plan.period_end)}
        </Typography>

        {plan.participants.length === 0 ? (
          <Typography fontSize="0.8rem" color="#94A3B8" sx={{ py: 3, textAlign: 'center' }}>No sales credited yet for this plan.</Typography>
        ) : pooled ? (
          plan.participants.map((p) => {
            const pct = pctOf(p.achievement_pct);
            return (
              <Box key={`${p.participant_type}-${p.participant_id}`} sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 2, mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Storefront sx={{ color: '#4F46E5', fontSize: 20 }} />
                  <Typography fontWeight={700} fontSize="0.85rem" color="#1E293B" noWrap>{p.name}</Typography>
                </Stack>
                {pct === null && p.sold_so_far !== undefined ? (
                  // Amounts by sales value (e.g. the daily store incentive): earned so far and what the next level needs.
                  <>
                    <Typography fontWeight={800} fontSize="1.5rem" color={parseFloat(p.payout || 0) > 0 ? '#16A34A' : '#1E293B'}>
                      {inr(p.payout)} earned
                    </Typography>
                    <Typography fontSize="0.8rem" color="#1E293B" sx={{ mt: 0.5 }}>Sold {inr(p.sold_so_far)}</Typography>
                    {p.next_level_at ? (
                      <>
                        <Bar pct={(parseFloat(p.sold_so_far) / parseFloat(p.next_level_at)) * 100} />
                        <Typography fontSize="0.72rem" color="#64748B" sx={{ mt: 0.75 }}>
                          {inr(parseFloat(p.next_level_at) - parseFloat(p.sold_so_far))} more for {inr(p.next_level_amount)} · shared by the team
                        </Typography>
                      </>
                    ) : (
                      <Typography fontSize="0.72rem" color="#16A34A" fontWeight={700} sx={{ mt: 0.75 }}>Top level reached · shared by the team</Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography fontWeight={800} fontSize="1.5rem" color={pct !== null && pct >= 100 ? '#16A34A' : '#1E293B'}>
                      {pct === null ? 'No target' : `${Math.round(pct)}%`}
                    </Typography>
                    <Bar pct={pct} />
                    <Typography fontSize="0.72rem" color="#64748B" sx={{ mt: 0.75 }}>
                      of this period's target · the team pool is paid after the period closes
                    </Typography>
                  </>
                )}
              </Box>
            );
          })
        ) : (
          <Stack spacing={1}>
            {plan.participants.map((p) => {
              const pct = pctOf(p.achievement_pct);
              const isMe = p.participant_id === me;
              return (
                <Box key={`${p.participant_type}-${p.participant_id}`}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.25, bgcolor: isMe ? '#FAFAFE' : '#FFF', borderLeft: isMe ? '3px solid #4F46E5' : undefined }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: '0.72rem', fontWeight: 800,
                      bgcolor: p.rank === 1 ? '#EEF2FF' : '#F1F5F9', color: p.rank === 1 ? '#4F46E5' : '#64748B' }}>{p.rank}</Box>
                    <Avatar sx={{ width: 26, height: 26, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#E0E7FF', color: '#4F46E5' }}>{(p.name || '?').charAt(0)}</Avatar>
                    <Typography fontWeight={700} fontSize="0.85rem" color="#1E293B" noWrap sx={{ minWidth: 0, flex: 1 }} title={p.name}>{p.name}</Typography>
                    {isMe && <Chip label="You" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5' }} />}
                    <Typography fontWeight={800} fontSize="0.85rem" color="#1E293B" sx={{ minWidth: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {p.payout === null || p.payout === undefined ? '—' : inr(p.payout)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, pl: '62px' }}>
                    <Box sx={{ flex: 1 }}><Bar pct={pct} /></Box>
                    <Typography fontSize="0.72rem" fontWeight={700} sx={{ minWidth: 56, textAlign: 'right', color: pct !== null && pct >= 100 ? '#16A34A' : '#64748B' }}>
                      {pct === null ? 'No target' : `${Math.round(pct)}%`}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
        <Typography fontSize="0.7rem" color="#94A3B8" sx={{ mt: 1.5 }}>
          Totals for the whole plan, including sales at other stores. Bills with no salesperson earn nobody.
        </Typography>
      </Box>
    );
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmojiEvents sx={{ color: '#4F46E5' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize="1rem" color="#1E293B">Incentives</Typography>
          <Typography fontSize="0.72rem" color="#64748B" noWrap>
            {storeName}{fulfilledTimeStamp ? ` · updated ${timeLabel(new Date(fulfilledTimeStamp))}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton aria-label="Refresh" onClick={refetch} disabled={isFetching} sx={{ width: 40, height: 40, color: '#64748B' }}>
              <Refresh fontSize="small" sx={{ animation: isFetching && !isLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton aria-label="Close" onClick={onClose} sx={{ width: 40, height: 40, color: '#64748B' }}><Close fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>{body()}</Box>
      <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0', pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
        <Button fullWidth variant="contained" disableElevation onClick={onClose}
          sx={{ bgcolor: '#4F46E5', fontWeight: 700, textTransform: 'none', borderRadius: '8px', height: 44, '&:hover': { bgcolor: '#4338CA' } }}>
          Done
        </Button>
      </Box>
    </Drawer>
  );
}
